import PropTypes from "prop-types"
import { createContext, useCallback, useContext, useMemo, useState } from "react"
import { FilterList, PivotTableChart, ViewColumn } from "@mui/icons-material"
import { OpenInNew } from "@mui/icons-material"
import { Box, Button } from "@mui/material"
import { ColumnsPanelTrigger, FilterPanelTrigger, PivotPanelTrigger, ToolbarButton } from "@mui/x-data-grid-premium"
import { useAnalytics } from "connected-components/analytics"
import { useIsAiModulePinningEnabled } from "resources/account"
import { ARTIFACT_KINDS } from "connected-components/dashboard/modules/ai-artifact/types"
import Tooltip from "components/material/tooltip"
import { useTableArtifactData } from "../../helpers/use-table-artifact-data"
import { TABLE_ARTIFACT } from "../../helpers/shapes"
import { AiContext } from "../../context/ai-context"
import pluralize from "pluralize"
import { AI_TEAMMATE_EVENTS } from "../../analytics"
import ArtifactDataGrid from "./components/artifact-data-grid"
import DashboardToolbar from "./components/dashboard-toolbar"
import ExplainSteps from "./components/explain-steps"
import ExportMenu from "./components/toolbar-components/export-menu"
import QuickFilter from "./components/toolbar-components/quick-filter"
import FullSizeTable from "./components/full-size-table"
import PreviewTable from "./components/preview-table"
import PinArtifactButton from "../pin-artifact-button"
import "./dashboard-table-artifact.scss"
import "./style.scss"

const DashboardToolbarContext = createContext(null)

const BASE_TOOLBAR_BUTTONS = (
  <>
    <Tooltip title="Columns">
      <span>
        <ColumnsPanelTrigger render={<ToolbarButton />}>
          <ViewColumn fontSize="small" />
        </ColumnsPanelTrigger>
      </span>
    </Tooltip>
    <Tooltip title="Filters">
      <span>
        <FilterPanelTrigger render={<ToolbarButton />}>
          <FilterList fontSize="small" />
        </FilterPanelTrigger>
      </span>
    </Tooltip>
    <Tooltip title="Pivot">
      <span>
        <PivotPanelTrigger render={<ToolbarButton />}>
          <PivotTableChart fontSize="small" />
        </PivotPanelTrigger>
      </span>
    </Tooltip>
  </>
)

const RIGHT_TOOLBAR_BUTTONS = (
  <>
    <ExportMenu />
    <QuickFilter />
  </>
)

function DashboardTableToolbar(props) {
  const context = useContext(DashboardToolbarContext)
  if (!context) return null
  const { explainSteps, isExplainOpen, onExplainToggle } = context
  return (
    <DashboardToolbar
      {...props}
      baseToolbarButtons={BASE_TOOLBAR_BUTTONS}
      rightToolbarButtons={RIGHT_TOOLBAR_BUTTONS}
      explainSteps={explainSteps}
      onExplainToggle={onExplainToggle}
      isExplainOpen={isExplainOpen}
    />
  )
}

export default function TableArtifact({
  artifact,
  variant = "chat",
  currency,
  dataGridState,
  onDataGridStateChange,
}) {
  const analytics = useAnalytics()
  const [fullScreenTableOpen, setFullScreenTableOpen] = useState(false)
  const [isExplainOpen, setIsExplainOpen] = useState(false)
  const isAiModulePinningEnabled = useIsAiModulePinningEnabled()
  const aiContext = useContext(AiContext)
  const canPinArtifacts = aiContext?.canPinArtifacts ?? false

  const { columns, rows } = useTableArtifactData(artifact, currency)
  const explainSteps = artifact.explainSteps

  const handleExplainToggle = useCallback(() => setIsExplainOpen(prev => !prev), [])

  const toggleFullScreenTable = useCallback(() => {
    if (!fullScreenTableOpen) analytics.track(AI_TEAMMATE_EVENTS.TABLE_EXPANDED)
    setFullScreenTableOpen(prev => !prev)
  }, [analytics, fullScreenTableOpen])

  const toolbarContextValue = useMemo(
    () => ({ explainSteps, isExplainOpen, onExplainToggle: handleExplainToggle }),
    [explainSteps, isExplainOpen, handleExplainToggle],
  )

  // Dashboard variant: full DataGrid with toolbar, no preview
  if (variant === "dashboard") {
    return (
      <DashboardToolbarContext.Provider value={toolbarContextValue}>
        <div className="component_dashboard-table-artifact">
          <ArtifactDataGrid
            columns={columns}
            rows={rows}
            className="component_dashboard-table-artifact__grid"
            toolbarComponents={undefined}
            customToolbar={DashboardTableToolbar}
            onPivotWarning={undefined}
            reducedPadding
            initialState={dataGridState}
            onStateChange={onDataGridStateChange}
          />
          {explainSteps && isExplainOpen && (
            <div className="component_dashboard-table-artifact__explain-panel">
              <ExplainSteps explainSteps={explainSteps} defaultExpanded hideTrigger />
            </div>
          )}
        </div>
      </DashboardToolbarContext.Provider>
    )
  }

  // Chat variant: preview table + expand to full modal
  const viewButtonText = rows.length === 1
    ? "View 1 row"
    : `View all ${rows.length} ${pluralize("row", rows.length)}`

  return <>
    <Box className="ai-teammate-table-artifact">
      <div>Found {rows.length} {pluralize("result", rows.length)}:</div>
      <PreviewTable columns={columns} rows={rows} />
      <Box className="table-artifact-actions">
        <Button size="small" className="view-all-button" variant="outlined" color="primary" startIcon={<OpenInNew />} onClick={toggleFullScreenTable}>
          {viewButtonText}
        </Button>
        {isAiModulePinningEnabled && canPinArtifacts && <PinArtifactButton artifact={artifact} kind={ARTIFACT_KINDS.TABLE} />}
      </Box>
    </Box>
    <FullSizeTable
      open={fullScreenTableOpen}
      onClose={toggleFullScreenTable}
      columns={columns}
      rows={rows}
      explainSteps={explainSteps}
    />
  </>
}

TableArtifact.propTypes = {
  artifact: TABLE_ARTIFACT.isRequired,
  variant: PropTypes.oneOf(["chat", "dashboard"]),
  currency: PropTypes.string,
  dataGridState: PropTypes.object,
  onDataGridStateChange: PropTypes.func,
}
